import { useState } from 'react';
import { restDefaultHandlers } from '@hotjar/webapp/utils/rest-default-handlers';
import { useCurrentUser } from '@hotjar/user';
import { useNotify } from '@hotjar/hotsauce';
import { useUserLogin } from '@hotjar/auth/src/domains/authentication/useUserLogin';
import type { UseFormMethods } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useTranslation } from '@hotjar/translations';
import { deleteProfile } from '../../../domains/profile/api';

export type FormData = {
  password?: string;
  email?: string;
  confirmDelete: boolean;
  confirmAccountDelete: boolean;
};

type FormMethods = UseFormMethods<FormData>;

export const useDeleteAccount = (): {
  isSSOOnly: boolean;
  isAccountAdmin: boolean;
  onSubmit: ReturnType<FormMethods['handleSubmit']>;
  register: FormMethods['register'];
  errors: FormMethods['errors'];
  control: FormMethods['control'];
  isLoading: boolean;
} => {
  const { t } = useTranslation('profile');
  const { data: currentUser } = useCurrentUser();
  const { logoutAndClearData } = useUserLogin();
  const { showToast } = useNotify();
  const [isLoading, setIsLoading] = useState(false);

  const isSSOOnly = currentUser?.isSSOOnly === true;
  const isAccountAdmin = currentUser?.isAccountAdmin === true;
  const { register, handleSubmit, errors, setError, control } = useForm<FormData>({
    mode: 'onBlur',
  });

  const onDelete = (data: FormData) => {
    if (currentUser && currentUser.email !== null) {
      const email = data.email === undefined ? currentUser.email : data.email;
      setIsLoading(true);
      deleteProfile(currentUser?.id, email, data.password)
        .then((response) => {
          if (response.success) {
            const urlRedirect = isAccountAdmin
              ? '/login?deleted=account_owner'
              : '/login?deleted=user';

            logoutAndClearData(() => {
              showToast({
                text: t<string>('delete-account-modal.success-message'),
                kind: 'success',
              });
              setTimeout(() => {
                window.location.assign(urlRedirect);
              }, 300);
            });
          } else if (response.incorrectEmail) {
            setError('email', { message: t<string>('delete-account-modal.email-error') });
          } else if (response.error === 'Invalid email / password combination.') {
            setError('password', { message: t<string>('delete-account-modal.password-error') });
          } else {
            showToast({ text: t<string>('delete-account-modal.unknown-error'), kind: 'danger' });
          }
        })
        .catch(restDefaultHandlers.onError)
        .finally(() => setIsLoading(false));
    }
  };

  const onSubmit = handleSubmit(onDelete);

  return { isSSOOnly, isAccountAdmin, onSubmit, register, errors, control, isLoading };
};
